import { EMPTY_OBJ } from '@dadohr/shared/domainTypes/emptyConstants';
import styled from '@emotion/styled';
import React from 'react';

export const LegendLabel = styled('span')<{ size?: 'small' | 'normal' }>`
  color: ${(p) => p.theme.custom.colors.gray.w400};
  font-size: ${(p) =>
    p.size === 'small'
      ? p.theme.custom.fontSize.s
      : p.theme.custom.fontSize.m}px;
`;

const ShapeContainer = styled('div')<{
  small: boolean;
  wrap?: string; // 'true' | undefined
  narrow: boolean;
}>`
  display: flex;
  align-items: center;
  white-space: ${(p) => (p.wrap ? 'wrap' : 'nowrap')};
  gap: ${(p) => p.theme.spacing(p.small || p.narrow ? 0.5 : 1)}px;
`;

const Square = styled('div')<{ color: string; small: boolean }>`
  background-color: ${(p) => p.color};
  border-radius: 2px;
  height: ${(p) => (p.small ? 10 : 12)}px;
  width: ${(p) => (p.small ? 10 : 12)}px;
  flex-shrink: 0;
  align-self: center;
`;

const Circle = styled(Square)`
  border-radius: 50%;
`;

const ELLIPSIS_STYLE = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

export const WithShape: React.FC<{
  color: string;
  small?: boolean;
  narrow?: boolean;
  shape?: 'circle' | 'square';
  wrap?: boolean;
  ellipsis?: boolean;
  containerStyle?: React.CSSProperties;
}> = ({
  color,
  small,
  narrow,
  shape = 'circle',
  wrap,
  ellipsis,
  containerStyle,
  children
}) => {
  return (
    <ShapeContainer
      wrap={wrap ? 'true' : undefined}
      small={!!small}
      narrow={!!narrow}
      style={containerStyle}
    >
      {shape === 'circle' ? (
        <Circle color={color} small={!!small} />
      ) : (
        <Square color={color} small={!!small} />
      )}
      <div style={ellipsis ? ELLIPSIS_STYLE : EMPTY_OBJ}>{children}</div>
    </ShapeContainer>
  );
};

// about stacking
// - ideally items would have a border
//
// problems:
// - we'd need to increase the size to offset the border width
// - we would need to define the color of the border from the outside
const StackedShapes = styled('div')((p) => ({
  display: 'inline-flex',
  flexDirection: 'row-reverse',
  justifyContent: 'center',
  alignItems: 'center',

  '> :not(:last-child)': {
    marginLeft: -p.theme.spacing(0.5)
  }
}));

export const WithShapes: React.FC<{
  colors: string[];
  small?: boolean;
  narrow?: boolean;
  shape?: 'circle' | 'square';
  ellipsis?: boolean;
}> = ({ colors, small, narrow, shape = 'circle', ellipsis, children }) => {
  return (
    <ShapeContainer small={!!small} narrow={!!narrow}>
      <StackedShapes>
        {colors.map((color, i) =>
          shape === 'circle' ? (
            <Circle key={i} color={color} small={!!small} />
          ) : (
            <Square key={i} color={color} small={!!small} />
          )
        )}
      </StackedShapes>
      <div style={ellipsis ? ELLIPSIS_STYLE : EMPTY_OBJ}>{children}</div>
    </ShapeContainer>
  );
};
